/**
 * NLFX Cable Builder Utility Service
 * Centralized utility functions used across the application
 */

const { compatibilityMatrix, areConnectorsCompatible } = require('./compatibilityMatrix');

/**
 * Converts inches to a feet and inches display format
 * @param {number} inches - Length in inches
 * @returns {string} Formatted string in feet and inches
 */
function toFeetInches(inches) {
  const totalInches = Math.round(inches);
  const feet = Math.floor(totalInches / 12);
  const inches2 = totalInches % 12;
  return `${feet}' ${inches2}"`;
}

/**
 * Converts between feet and inches
 * @param {number} value - The value to convert
 * @param {string} fromUnit - The unit to convert from ('ft' or 'in')
 * @param {string} toUnit - The unit to convert to ('ft' or 'in')
 * @returns {number} The converted value
 */
function convertLength(value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;
  
  if (fromUnit === 'ft' && toUnit === 'in') {
    return value * 12;
  } else if (fromUnit === 'in' && toUnit === 'ft') {
    return value / 12;
  }
  
  return value;
}

/**
 * Finds a connector in the connector list and returns its SKU code (robust, always simplified for XLR)
 * @param {object} connector - The connector object to find
 * @returns {string|undefined} The SKU code if found, undefined otherwise
 */
function findConnectorWithSkuCode(connector) {
  if (!connector) {
    return 'UNKNOWN';
  }
  
  // Handle different connector object formats
  let id;
  if (typeof connector === 'string') {
    id = connector;
  } else if (connector.connector) {
    id = connector.connector; // BaseConnectorSelector uses 'connector' property
  } else if (connector.id) {
    id = connector.id; // Some components use 'id' property
  } else {
    return 'UNKNOWN';
  }
  
  // First try with the exact id
  let matrixEntry = compatibilityMatrix[id];
  
  // If not found, try with the label if it exists and is different
  if (!matrixEntry && connector.label && connector.label !== id) {
    matrixEntry = compatibilityMatrix[connector.label];
  }
  
  // If still not found, try to find by family and gender with configuration matching
  if (!matrixEntry && connector.family && connector.gender) {
    console.log('DEBUG: Trying to find by family and gender:', connector.family, connector.gender);
    
    // Get the configuration to match the correct mount type
    const configuration = connector.configuration || connector.variant || 'standard';
    let targetMountType = 'cable'; // default
    
    if (configuration === 'panel-mount') {
      targetMountType = 'panel';
    } else if (configuration === 'right-angle') {
      targetMountType = 'rightangle';
    }
    
    console.log('DEBUG: Looking for mountType:', targetMountType, 'with configuration:', configuration);
    
    const matchingEntries = Object.values(compatibilityMatrix).filter(entry => 
      entry.family === connector.family && 
      entry.gender === connector.gender &&
      entry.mountType === targetMountType
    );
    console.log('DEBUG: Found matching entries by family/gender/mountType:', matchingEntries);
    if (matchingEntries.length > 0) {
      matrixEntry = matchingEntries[0]; // Use the first match
      console.log('DEBUG: Using first match:', matrixEntry);
    }
  }
  
  // If still not found, try broader family/gender match without mount type
  if (!matrixEntry && connector.family && connector.gender) {
    console.log('DEBUG: Trying broader family/gender match');
    const matchingEntries = Object.values(compatibilityMatrix).filter(entry => 
      entry.family === connector.family && entry.gender === connector.gender
    );
    console.log('DEBUG: Found matching entries by family/gender (broader):', matchingEntries);
    if (matchingEntries.length > 0) {
      matrixEntry = matchingEntries[0]; // Use the first match
      console.log('DEBUG: Using first broader match:', matrixEntry);
    }
  }
  
  // If we have a matrix entry with skuCode, return it
  if (matrixEntry && matrixEntry.skuCode) {
    console.log('DEBUG: Found SKU code:', matrixEntry.skuCode);
    return matrixEntry.skuCode;
  }
  
  // If we have a matrix entry but no skuCode, try to use the id as skuCode
  if (matrixEntry && matrixEntry.id) {
    console.log('DEBUG: Using matrix entry id as SKU code:', matrixEntry.id);
    return matrixEntry.id;
  }
  
  // If we have a connector with skuCode property, use it
  if (connector.skuCode) {
    console.log('DEBUG: Using connector skuCode property:', connector.skuCode);
    return connector.skuCode;
  }
  
  // Last resort: if we have an id but no matrix entry, try to use the id itself
  if (id && id !== 'UNKNOWN') {
    console.log('DEBUG: Using connector id as SKU code:', id);
    return id;
  }
  
  console.log('DEBUG: No SKU code found, returning UNKNOWN');
  return 'UNKNOWN';
}

/**
 * Dynamically determines which configurations are available for a connector
 * by looking through the compatibility matrix for variants with the same family, type, and purpose
 * but different mountType values.
 * 
 * @param {object} connector - The connector object to check
 * @returns {object} Object with boolean values for each configuration type
 */
function getAvailableConfigurations(connector) {
  if (!connector) {
    return { standard: true, 'right-angle': true, 'panel-mount': true };
  }

  // Get the connector ID - handle different connector object formats
  const connectorId = connector.connector || connector.id;
  if (!connectorId) {
    return { standard: true, 'right-angle': true, 'panel-mount': true };
  }

  // Get the connector data from the compatibility matrix
  const connectorData = compatibilityMatrix[connectorId];
  if (!connectorData) {
    console.warn(`Connector not found in compatibility matrix: ${connectorId}`);
    return { standard: true, 'right-angle': true, 'panel-mount': true };
  }

  const { family, type, purpose, gender, poles } = connectorData;
  
  // Search through the compatibility matrix to find all variants with the same family, type, purpose, gender, AND poles
  const availableMountTypes = new Set();
  
  Object.values(compatibilityMatrix).forEach(connector => {
    // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
    const connectorPurposes = connector.purpose ? 
      connector.purpose.split(',').map(p => p.trim().toLowerCase()) : 
      [];
    
    const targetPurposes = purpose ? 
      purpose.split(',').map(p => p.trim().toLowerCase()) : 
      [];
    
    // Check if any of the connector's purposes match any of the target purposes
    const purposeMatches = connectorPurposes.some(connectorPurpose => 
      targetPurposes.includes(connectorPurpose)
    );
    
    if (connector.family === family && 
        connector.type === type && 
        purposeMatches &&
        connector.gender === gender &&
        connector.poles === poles) {
      availableMountTypes.add(connector.mountType);
    }
  });

  // Map mountType values to configuration names
  const hasStandard = availableMountTypes.has('cable');
  const hasRightAngle = availableMountTypes.has('rightangle') || availableMountTypes.has('right-angle');
  const hasPanelMount = availableMountTypes.has('panel');

  console.log(`DEBUG: getAvailableConfigurations for ${connectorId}:`, {
    family,
    type,
    purpose,
    gender,
    availableMountTypes: Array.from(availableMountTypes),
    configurations: {
      standard: hasStandard,
      'right-angle': hasRightAngle,
      'panel-mount': hasPanelMount
    }
  });

  return {
    standard: hasStandard,
    'right-angle': hasRightAngle,
    'panel-mount': hasPanelMount
  };
}

/**
 * Generates a SKU for a cable based on its configuration
 * @param {object} config - The cable configuration
 * @returns {string} The generated SKU with timestamp for uniqueness
 */
function generateSku(config) {
  const { type, style, connectors, hybridConnectors, length } = config;
  
  // Base SKU prefix based on cable type and style
  let skuPrefix = 'NLFX-';
  
  // Add cable type code
  switch (type) {
    case 'audio':
      skuPrefix += 'A';
      break;
    case 'power':
      skuPrefix += 'P';
      break;
    case 'hybrid':
      skuPrefix += 'H';
      break;
    default:
      skuPrefix += 'X';
  }
  
  // Add style code
  switch (style) {
    case 'standard':
      skuPrefix += 'S';
      break;
    case 'wye':
      skuPrefix += 'W';
      break;
    case 'zip':
      skuPrefix += 'Z';
      break;
    case 'zip-audio-audio':
      skuPrefix += 'Z';
      break;
    case 'hybrid':
      skuPrefix += 'H';
      break;
    default:
      skuPrefix += 'X';
  }
  
  // Handle connector SKUs
  let connectorCodes = '-';
  
  if (style === 'standard') {
    // Standard cable with end A and end B
    const endASku = findConnectorWithSkuCode(connectors.endA);
    const endBSku = findConnectorWithSkuCode(connectors.endB);
    connectorCodes += `${endASku || 'XX'}-${endBSku || 'XX'}`;
  } else if (['hybrid', 'zip', 'zip-audio-audio', 'wye'].includes(style)) {
    // For complex cables with multiple connectors
    if (hybridConnectors && hybridConnectors.connectors) {
      const connF = findConnectorWithSkuCode(hybridConnectors.connectors.F);
      const connG = findConnectorWithSkuCode(hybridConnectors.connectors.G);
      const connH = findConnectorWithSkuCode(hybridConnectors.connectors.H);
      const connI = findConnectorWithSkuCode(hybridConnectors.connectors.I);
      
      connectorCodes += `${connF || 'XX'}-${connG || 'XX'}`;
      
      if (style === 'hybrid' || style === 'zip') {
        connectorCodes += `-${connH || 'XX'}-${connI || 'XX'}`;
      }
    }
  }
  
  // Add length in feet (rounded to nearest foot)
  const lengthFeet = Math.round(length);
  
  return `${skuPrefix}${connectorCodes}-${lengthFeet}FT`;
}

/**
 * Simple connector ID extractor - no mapping needed since IDs now match matrix exactly
 */
function getConnectorId(connector) {
  if (!connector) return undefined;
  
  // If it's already a string ID, return as is
  if (typeof connector === 'string') {
    return connector;
  }
  
  // Return the connector ID directly - no mapping needed
  return connector.id;
}

/**
 * Checks compatibility between two connectors using the compatibilityMatrix.
 * Returns standardized result: { status, message, notes }
 * - status: 'compatible' | 'warning' | 'incompatible'
 * - message: warning or incompatibility reason
 * - notes: additional notes
 */
function getConnectorPairCompatibility(connectorAId, connectorBId) {
  try {
    // Get connector IDs directly - no mapping needed since IDs now match matrix exactly
    const connectorA = getConnectorId(connectorAId);
    const connectorB = getConnectorId(connectorBId);
    
    console.log('🔍 getConnectorPairCompatibility direct check:', {
      connectorA,
      connectorB,
      connectorAId,
      connectorBId
    });
    
    // Handle undefined or null connector IDs
    if (!connectorA || !connectorB) {
      console.warn('⚠️ getConnectorPairCompatibility: Missing connector ID', {
        connectorA,
        connectorB,
        connectorAId,
        connectorBId
      });
      return {
        status: 'incompatible',
        message: connectorA ? 'Other connector not selected' : 'Connector not selected',
        notes: null
      };
    }
    
    // Use the compatibility matrix directly as the single source of truth
    const result = areConnectorsCompatible(connectorA, connectorB);
    
    console.log('🔍 Compatibility matrix result:', {
      connectorA,
      connectorB,
      result
    });
    
    if (!result) {
      return {
        status: 'incompatible',
        message: 'Connector not found in matrix',
        notes: null
      };
    }
    
    if (result.compatible === false) {
      return {
        status: 'incompatible',
        message: result.reason || 'Incompatible',
        notes: result.details || null
      };
    }
    
    if (result.compatible === true) {
      if (result.warning) {
        return {
          status: 'warning',
          message: result.warning,
          notes: result.notes || null
        };
      }
      return {
        status: 'compatible',
        message: null,
        notes: result.notes || null
      };
    }
    
    // This shouldn't happen, but just in case
    return {
      status: 'incompatible',
      message: 'Unknown compatibility status',
      notes: null
    };
  } catch (error) {
    console.error('Error in getConnectorPairCompatibility:', error);
    return {
      status: 'incompatible',
      message: 'Error checking compatibility',
      notes: null
    };
  }
}

/**
 * Loads an image from a URL and returns a base64 data URI (client-side only)
 * @param {string} url - The image URL (relative or absolute)
 * @returns {Promise<string>} - The base64 data URI
 */
async function imageUrlToBase64DataUri(url) {
  // Handle null/undefined/empty URL
  if (!url || typeof url !== 'string') {
    console.warn('imageUrlToBase64DataUri: Invalid URL provided:', url);
    return null;
  }
  
  // If already a data URI, return as is
  if (url.startsWith('data:')) return url;
  // Fetch the image as a blob
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch image for base64 conversion: ' + url);
  const blob = await response.blob();
  // Convert blob to base64 using FileReader
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

module.exports = {
  toFeetInches,
  convertLength,
  findConnectorWithSkuCode,
  getAvailableConfigurations,
  generateSku,
  getConnectorPairCompatibility,
  imageUrlToBase64DataUri
};
